import { useLinkTo, useNavigation } from '@react-navigation/native';
import { useGlobalConfig } from 'context/GlobalContext';
import { useEffect, useLayoutEffect } from 'react';
import { Modal, StyleSheet, View, useWindowDimensions } from 'react-native';
import { createPortal } from 'react-dom';
import { Platform } from 'utils/platform';

import { usePlayer } from 'components/Player/context';
import ErrorScreenBase from 'screens/ErrorScreen/ErrorScreenBase';
import { getErrorData } from 'services/errorService';

export enum ErrorHandlerType {
  NO_INTERNET = 'NO_INTERNET',
  NETWORK_ERROR = 'NETWORK_ERROR',
  GEOLOCATION_ERROR = 'GEOLOCATION_ERROR',
  NOT_FOUND = 'NOT_FOUND',
}
export interface ErrorHandlerProps {
  error: ErrorHandlerType | undefined;
}

const TV_ERROR_PORTAL_ID = 'bandplay-tv-error-portal';
const ERROR_SCREEN_DOM_ID = 'bandplay-error-screen';

let tvErrorPortalRefCount = 0;

function isRemoteEnterLike(e: KeyboardEvent): boolean {
  if (e.key === 'Enter' || e.key === 'Select') {
    return true;
  }
  const c = e.keyCode || e.which || 0;
  return c === 13 || c === 415 || c === 65385;
}

function getTvErrorPortalParent(): HTMLElement | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const root = document.getElementById('root');
  if (!root) {
    return null;
  }
  let mount = document.getElementById(TV_ERROR_PORTAL_ID);
  if (!mount) {
    mount = document.createElement('div');
    mount.id = TV_ERROR_PORTAL_ID;
    mount.className = 'navigation-container navigation-priority';
    root.insertBefore(mount, root.firstChild);
  }
  return mount;
}

const ErrorHandler: React.FC<ErrorHandlerProps> = (props) => {
  const { error = undefined } = props;
  const {
    reloadMedia,
    setError,
    setIsVisible,
    setHasTransmissionError,
    setFullScreen,
  } = usePlayer();
  const linkTo = useLinkTo();
  const navigation = useNavigation();
  const { hasInternet } = useGlobalConfig();
  const { width } = useWindowDimensions();
  const isMobile = width < 950;
  const shouldShowError =
    !!error && (error !== ErrorHandlerType.NO_INTERNET || !hasInternet);

  const clearError = () => {
    setIsVisible(false);
    setFullScreen(false);
    setError(undefined);
    setHasTransmissionError(false);
  };

  const handleGoHome = () => {
    clearError();
    linkTo('/');
  };

  const handleReload = () => {
    reloadMedia();
    clearError();
  };

  const handleRetry = () => {
    clearError();
  };

  const renderErrorScreen = () => {
    const errorData = getErrorData(error, isMobile, Platform.isTV);
    switch (error) {
      case ErrorHandlerType.NO_INTERNET:
        return (
          <ErrorScreenBase
            title={errorData.title}
            description={errorData.description}
            buttonText={errorData.buttonTxt}
            onButtonPress={errorData.buttonTxt ? handleRetry : undefined}
          />
        );
      case ErrorHandlerType.NETWORK_ERROR:
        return (
          <ErrorScreenBase
            title={errorData.title}
            description={errorData.description}
            buttonText={errorData.buttonTxt}
            onButtonPress={errorData.buttonTxt ? handleReload : undefined}
          />
        );
      case ErrorHandlerType.NOT_FOUND:
      case ErrorHandlerType.GEOLOCATION_ERROR:
      default:
        return (
          <ErrorScreenBase
            title={errorData.title}
            description={errorData.description}
            buttonText={errorData.buttonTxt}
            onButtonPress={errorData.buttonTxt ? handleGoHome : undefined}
          />
        );
    }
  };

  useEffect(() => {
    return navigation.addListener('beforeRemove', async () => {
      clearError();
    });
  }, []);

  useEffect(() => {
    if (!shouldShowError) {
      setHasTransmissionError(false);
      return;
    }

    setIsVisible(false);
    setHasTransmissionError(error !== ErrorHandlerType.NO_INTERNET ? true : !hasInternet);
  }, [error, hasInternet, setHasTransmissionError, setIsVisible, shouldShowError]);

  useEffect(() => {
    if (Platform.OS !== 'web' || Platform.isTV || !shouldShowError) {
      return;
    }
    const root = document.getElementById('root');
    if (!root) {
      return;
    }
    root.setAttribute('inert', '');
    return () => root.removeAttribute('inert');
  }, [shouldShowError]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !Platform.isTV || !shouldShowError) {
      return;
    }
    tvErrorPortalRefCount += 1;
    return () => {
      tvErrorPortalRefCount -= 1;
      if (tvErrorPortalRefCount <= 0) {
        tvErrorPortalRefCount = 0;
        document.getElementById(TV_ERROR_PORTAL_ID)?.remove();
      }
    };
  }, [shouldShowError]);

  useLayoutEffect(() => {
    if (Platform.OS !== 'web' || !Platform.isTV || !shouldShowError) {
      return;
    }
    let cancelled = false;
    let raf2 = 0;
    let snapTimeout = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        window.dispatchEvent(new Event('navigation'));
        snapTimeout = window.setTimeout(() => {
          if (cancelled) {
            return;
          }
          void import('utils/ports/keyboard').then(({ tvSnapFocusToElement }) => {
            if (cancelled) {
              return;
            }
            const btn = document.querySelector<HTMLButtonElement>(
              `#${ERROR_SCREEN_DOM_ID} button[type="button"]`
            );
            if (btn) {
              tvSnapFocusToElement(btn);
            }
          });
        }, 24);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimeout(snapTimeout);
    };
  }, [shouldShowError, error]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !shouldShowError) {
      return;
    }
    const onKeyDownCapture = (e: KeyboardEvent) => {
      if (!isRemoteEnterLike(e)) {
        return;
      }
      const root = document.getElementById(ERROR_SCREEN_DOM_ID);
      if (!root?.isConnected) {
        return;
      }
      const btn = root.querySelector<HTMLButtonElement>('button[type="button"]');
      if (!btn) {
        return;
      }
      const { width, height } = btn.getBoundingClientRect();
      if (width < 1 || height < 1) {
        return;
      }
      e.preventDefault();
      e.stopImmediatePropagation();
      btn.click();
    };
    window.addEventListener('keydown', onKeyDownCapture, true);
    return () => window.removeEventListener('keydown', onKeyDownCapture, true);
  }, [shouldShowError]);

  if (shouldShowError) {
    if (Platform.OS === 'web') {
      const portalParent = Platform.isTV ? getTvErrorPortalParent() ?? document.body : document.body;

      return createPortal(
        <div id={ERROR_SCREEN_DOM_ID} style={{ display: 'contents' }}>
          <View style={styles.errorOverlayRoot}>{renderErrorScreen()}</View>
        </div>,
        portalParent
      );
    }

    return (
      <Modal visible transparent animationType="none">
        <View style={styles.fullScreenOverlay}>{renderErrorScreen()}</View>
      </Modal>
    );
  }

  return null;
};

const styles = StyleSheet.create({
  fullScreenOverlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 9999,
    width: '100%',
    height: '100%',
    backgroundColor: '#070D31',
  },
  errorOverlayRoot: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    zIndex: 99999,
    display: 'flex',
    flexDirection: 'column',
  },
});

export default ErrorHandler;
